import { useContent } from "../hooks/useContent";
import { useInView } from "../hooks/useInView";
import { Badge } from "./ui/badge";
import { Sparkles, Shirt, Footprints, Check } from "lucide-react";

const iconMap = {
  sparkles: Sparkles,
  shirt: Shirt,
  footprints: Footprints,
};

const colorMap = {
  sparkles: { bg: "bg-sky-50", text: "text-sky-600", border: "border-sky-100", accent: "bg-sky-600" },
  shirt: { bg: "bg-lime-50", text: "text-lime-600", border: "border-lime-100", accent: "bg-lime-600" },
  footprints: { bg: "bg-orange-50", text: "text-orange-500", border: "border-orange-100", accent: "bg-orange-500" },
};

export const ServicesSection = () => {
  const [ref, isInView] = useInView();
  const { getSection } = useContent();
  const services = getSection("services") || [];

  return (
    <section id="services" className="py-16 lg:py-20 bg-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sky-50/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-lime-50/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" />

      {/* Laundry-themed floating bubbles */}
      <div className="absolute top-24 left-10 w-3 h-3 bg-sky-200/70 rounded-full animate-float pointer-events-none" />
      <div className="absolute top-40 right-16 w-4 h-4 bg-lime-200/60 rounded-full animate-float pointer-events-none" style={{ animationDelay: "0.8s" }} />
      <div className="absolute bottom-28 left-1/4 w-2.5 h-2.5 bg-sky-300/60 rounded-full animate-float pointer-events-none" style={{ animationDelay: "1.6s" }} />
      <div className="absolute bottom-16 right-1/3 w-3.5 h-3.5 bg-orange-200/50 rounded-full animate-float pointer-events-none" style={{ animationDelay: "0.4s" }} />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto mb-12" ref={ref}>
          <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-4">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-5">
            Three Services,
            <br />
            One Promise
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            Dry Cleaning, Laundry, and Shoe Cleaning — all handled with expertise and
            delivered within 24 hours.
          </p>
        </div>

        <div
          className={`grid md:grid-cols-3 gap-8 transition-all duration-700 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {services.map((service, i) => {
            const Icon = iconMap[service.iconKey];
            const colors = colorMap[service.iconKey];
            return (
              <div
                key={service.id}
                className="group relative bg-white rounded-3xl border border-slate-100 p-8 hover:border-sky-200 hover:shadow-xl hover:shadow-sky-900/5 transition-all duration-300"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div
                  className={`w-16 h-16 rounded-2xl ${colors.bg} ${colors.border} border flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon className={`w-7 h-7 ${colors.text}`} />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-6">
                  {service.description}
                </p>

                <div className="space-y-2.5 mb-6">
                  {service.features.map((f, j) => (
                    <div key={j} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-sky-50 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-sky-600" />
                      </div>
                      <span className="text-sm text-slate-600">{f}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <span className="text-lg font-bold text-sky-600">
                    {service.priceLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
